import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  ReconciliationAttempt,
  ReconciliationUpload,
  ReconciliationUploadRequest,
  ScheduleLaunch,
  ProgrammedActionDraft,
  Survey,
  SurveyActivityLogEntry,
  SurveyChannelCreationConfirmedEvent,
  SurveyComment,
  SurveyHealthMetric,
  SurveyQuota,
  SpecificAudienceReturn,
  SurveyQuotaUpdate,
  UploadPsidFileResult,
} from '@ps/survey-utils';
import { SurveyStatus } from '@ps/survey-kernel';

export const ManageSurveyActions = createActionGroup({
  source: 'Manage Survey',
  events: {
    'Load Survey Details': props<{ surveyId: number }>(),
    'Load Full Survey Details': props<{ surveyId: number }>(),
    'Load Child Survey Details': props<{ surveyId: number }>(),
    'Load Survey Health': props<{ surveyId: number }>(),
    'Load Remaining Hours': props<{ surveyId: number }>(),
    'Load Reconciliation Attempts': props<{ surveyId: number }>(),
    'Upload Reconciliation File': props<{
      request: ReconciliationUploadRequest;
      requestId: string;
    }>(),
    'Confirm Reconciliation': props<{ uploadId: string; surveyId: number }>(),
    'Decline Reconciliation': props<{ uploadId: string }>(),
    'Abandon Reconciliation Upload': emptyProps(),
    'Discard Reconciliation Upload': props<{ uploadId: string }>(),
    'Load Activity Logs': props<{ surveyId: number }>(),
    'Reset Activity Logs': emptyProps(),
    'Load Comments': props<{ surveyId: number }>(),
    'Add Comment': props<{
      surveyId: number;
      body: string;
      actions?: ProgrammedActionDraft[];
    }>(),
    'Reset Survey Details': emptyProps(),
    'Update Current Cpi': props<{
      surveyId: number;
      cpi: number;
      isChild: boolean;
    }>(),
    'Load Survey Quotas': props<{
      surveyId: number;
      page: number;
      search?: string;
    }>(),
    'Load More Quotas': props<{ surveyId: number }>(),
    'Load All Quotas': props<{ surveyId: number }>(),
    'Reset Survey Quotas': emptyProps(),
    'Set Quota Lock': props<{
      surveyId: number;
      psQuotaId: string;
      locked: boolean;
    }>(),
    'Set Quota Current Target': props<{
      surveyId: number;
      psQuotaId: string;
      currentTarget: number;
      updateSupplier: boolean;
    }>(),
    'Set Quota Currently Open': props<{
      surveyId: number;
      psQuotaId: string;
      currentlyOpen: number;
    }>(),
    'Set Supplier Quota Current Target': props<{
      surveyId: number;
      psQuotaId: string;
      supplierRefId: string;
      currentTarget: number;
    }>(),
    'Set Supplier Quota Currently Open': props<{
      surveyId: number;
      psQuotaId: string;
      supplierRefId: string;
      currentlyOpen: number;
    }>(),
    'Rename Quota': props<{
      surveyId: number;
      psQuotaId: string;
      label: string;
    }>(),
    'Set Quota Cpi': props<{
      surveyId: number;
      psQuotaId: string;
      cpi: number;
    }>(),
    'Load Traffic Channels': props<{ surveyId: number }>(),
    'Reset Traffic Channels': emptyProps(),
    'Set Traffic Channel Status': props<{
      surveyId: number;
      status: SurveyStatus;
    }>(),
    'Update Traffic Channel Cpi': props<{ surveyId: number; cpi: number }>(),
    'Launch Traffic Channel': props<{
      surveyId: number;
      event: SurveyChannelCreationConfirmedEvent;
      /** Where a Specific Audience's Edit Survey page returns to. */
      specificAudienceReturn: SpecificAudienceReturn;
    }>(),
    'Download Respondent List Template': emptyProps(),
    'Upload Psid File': props<{ surveyId: number; file: File }>(),
    'Clear Psid File': emptyProps(),
    'Remove Psid File': props<{ filePath: string }>(),
    'Discard Psid File': props<{ filePath: string }>(),
    'Update Survey': props<{ surveyId: number; survey: Partial<Survey> }>(),
    'Save Crm Id': props<{ surveyId: number; crmId: string }>(),
    'Save Asana Id': props<{ surveyId: number; asanaId: string }>(),
    'Save Billing Number': props<{
      surveyId: number;
      billingNumber: string;
    }>(),
    'Save Custom Redirect Field': props<{
      surveyId: number;
      value: string;
    }>(),
    'Clone Survey': props<{
      surveyId: number;
      isServiceOperatorCreation: boolean;
    }>(),
  },
});

export const ManageSurveyApiActions = createActionGroup({
  source: 'Manage Survey API',
  events: {
    'Load Survey Success': props<{ survey: Survey }>(),
    'Load Survey Failure': props<{ error: string }>(),
    'Load Child Survey Success': props<{ survey: Survey }>(),
    'Load Child Survey Failure': props<{ error: string }>(),
    'Load Survey Health Success': props<{
      healthMetrics: SurveyHealthMetric[];
    }>(),
    'Load Survey Health Failure': props<{ error: string }>(),
    'Load Remaining Hours Success': props<{ remainingHours: number }>(),
    'Load Remaining Hours Failure': props<{ error: string }>(),
    'Load Reconciliation Attempts Success': props<{
      reconciliationAttempts: ReconciliationAttempt[];
    }>(),
    'Load Reconciliation Attempts Failure': props<{ error: string }>(),
    'Upload Reconciliation File Success': props<{
      reconciliationUpload: ReconciliationUpload;
    }>(),
    'Upload Reconciliation File Failure': props<{ error: string }>(),
    'Confirm Reconciliation Success': emptyProps(),
    'Confirm Reconciliation Failure': props<{ error: string }>(),
    'Decline Reconciliation Success': emptyProps(),
    'Decline Reconciliation Failure': props<{ error: string }>(),
    'Load Activity Logs Success': props<{
      activityLogs: SurveyActivityLogEntry[];
    }>(),
    'Load Activity Logs Failure': props<{ error: string }>(),
    'Load Comments Success': props<{ comments: SurveyComment[] }>(),
    'Load Comments Failure': props<{ error: string }>(),
    'Add Comment Success': props<{ comment: SurveyComment }>(),
    'Add Comment Failure': props<{ error: string }>(),
    'Load Survey Quotas Success': props<{
      quotas: SurveyQuota[];
      total: number;
    }>(),
    'Load Survey Quotas Failure': props<{ error: string }>(),
    'Load More Quotas Success': props<{ quotas: SurveyQuota[] }>(),
    'Load More Quotas Failure': emptyProps(),
    'Load All Quotas Success': props<{ quotas: SurveyQuota[] }>(),
    'Load All Quotas Failure': emptyProps(),
    'Set Quota Lock Success': props<{ psQuotaId: string; locked: boolean }>(),
    'Set Quota Lock Failure': props<{ psQuotaId: string; error: string }>(),
    'Set Quota Current Target Success': props<{
      psQuotaId: string;
      update: SurveyQuotaUpdate;
    }>(),
    'Set Quota Current Target Failure': props<{
      psQuotaId: string;
      error: string;
    }>(),
    'Set Quota Currently Open Success': props<{
      psQuotaId: string;
      update: SurveyQuotaUpdate;
    }>(),
    'Set Quota Currently Open Failure': props<{
      psQuotaId: string;
      error: string;
    }>(),
    'Set Supplier Quota Current Target Success': props<{
      psQuotaId: string;
      supplierRefId: string;
      currentTarget: number;
    }>(),
    'Set Supplier Quota Current Target Failure': props<{
      psQuotaId: string;
      supplierRefId: string;
      error: string;
    }>(),
    'Set Supplier Quota Currently Open Success': props<{
      psQuotaId: string;
      supplierRefId: string;
      currentlyOpen: number;
    }>(),
    'Set Supplier Quota Currently Open Failure': props<{
      psQuotaId: string;
      supplierRefId: string;
      error: string;
    }>(),
    'Rename Quota Success': props<{ psQuotaId: string; label: string }>(),
    'Rename Quota Failure': props<{ psQuotaId: string; error: string }>(),
    'Set Quota Cpi Success': props<{
      surveyId: number;
      psQuotaId: string;
      cpi: number;
    }>(),
    'Set Quota Cpi Failure': props<{ psQuotaId: string; error: string }>(),
    'Load Traffic Channels Success': props<{ trafficChannels: Survey[] }>(),
    'Load Traffic Channels Failure': props<{ error: string }>(),
    'Poll Traffic Channels Failure': props<{ error: string }>(),
    'Set Traffic Channel Status Success': props<{
      surveyId: number;
      status: SurveyStatus;
    }>(),
    'Set Traffic Channel Status Failure': props<{
      surveyId: number;
      error: string;
    }>(),
    'Update Traffic Channel Cpi Success': props<{
      surveyId: number;
      cpi: number;
    }>(),
    'Update Traffic Channel Cpi Failure': props<{
      surveyId: number;
      error: string;
    }>(),
    'Launch Traffic Channel Success': props<{
      surveyId: number;
      trafficChannels: Survey[];
    }>(),
    'Launch Specific Audience Success': props<{
      surveyId: number;
      channelId: number;
      specificAudienceReturn: SpecificAudienceReturn;
    }>(),
    'Launch Traffic Channel Failure': props<{
      surveyId: number;
      error: string;
    }>(),
    'Download Respondent List Template Success': emptyProps(),
    'Download Respondent List Template Failure': props<{ error: string }>(),
    'Upload Psid File Success': props<UploadPsidFileResult>(),
    'Upload Psid File Failure': props<{ error: string }>(),
    'Remove Psid File Success': emptyProps(),
    'Remove Psid File Failure': props<{ error: string }>(),
    'Discard Psid File Success': emptyProps(),
    'Discard Psid File Failure': props<{ error: string }>(),
    'Save Crm Id Success': emptyProps(),
    'Save Crm Id Failure': props<{ error: string }>(),
    'Save Asana Id Success': emptyProps(),
    'Save Asana Id Failure': props<{ error: string }>(),
    'Save Billing Number Success': emptyProps(),
    'Save Billing Number Failure': props<{ error: string }>(),
    'Save Custom Redirect Field Success': props<{ value: string }>(),
    'Save Custom Redirect Field Failure': props<{ error: string }>(),
    'Clone Survey Success': props<{ survey: Survey }>(),
    'Clone Survey Failure': props<{ error: string }>(),
  },
});

// Dispatched only from the sibling-survey drawers (Multi Country, Grouped,
// Tracker) — kept separate from ManageSurveyActions because these edits
// target a row in the sibling table, not the primary survey the rest of this
// domain's actions operate on.
export const ManageSurveySiblingActions = createActionGroup({
  source: 'Manage Survey Sibling',
  events: {
    'Update Current Cpi': props<{
      surveyId: number;
      cpi: number;
      isChild: boolean;
    }>(),
    'Update Completes Required': props<{
      surveyId: number;
      completesRequired: number;
      isChild: boolean;
    }>(),
    'Update Schedule Launch': props<{
      surveyId: number;
      scheduleLaunch: ScheduleLaunch;
      isChild: boolean;
    }>(),
  },
});

export const ManageSurveySiblingApiActions = createActionGroup({
  source: 'Manage Survey Sibling API',
  events: {
    'Update Current Cpi Success': props<{ survey: Survey }>(),
    'Update Child Current Cpi Success': props<{ survey: Survey }>(),
    'Update Current Cpi Failure': props<{ error: string }>(),
    'Update Completes Required Success': props<{ survey: Survey }>(),
    'Update Child Completes Required Success': props<{ survey: Survey }>(),
    'Update Completes Required Failure': props<{ error: string }>(),
    'Update Schedule Launch Success': props<{ survey: Survey }>(),
    'Update Child Schedule Launch Success': props<{ survey: Survey }>(),
    'Update Schedule Launch Failure': props<{ error: string }>(),
  },
});
